<template>
    <div>
        <h1 class="mx-3 mt-3">eToro</h1>

        <rounded-container v-if="pending.length" variant="dark">
            <template v-slot:title>Pending orders</template>

            <b-row
                v-for="(order, rowIndex) in pendingRows"
                :key="rowIndex"
                :class="rowClass(rowIndex)"
                no-gutters
                class="p-3"
            >
                <b-col cols=4>
                    <b-icon :icon="sideIcon(order)" :class="sideClass(order)"></b-icon>&nbsp;
                    <span :class="{ 'font-weight-bold text-info': order.asset }">{{ order.symbol }}</span>
                </b-col>
                <b-col cols=3>
                    <span class="formatPrice text-warning">{{ formatPrice(order.price) }}</span>
                </b-col>
                <b-col cols=3>
                    <span class="formatPrice text-warning">${{ formatNumber(order.value) }}</span>
                </b-col>
                <b-col cols=2 class="text-secondary">
                    <span v-if="order.distance">{{ order.distance.formatted }}</span>
                </b-col>
            </b-row>
        </rounded-container>

        <rounded-container v-if="loading || apiError || !trades.length" variant="dark">
            <template v-slot:title>eToro trades</template>

            <div v-if="loading" class="text-center p-3">
                <b-spinner small type="grow" variant="success"></b-spinner>
            </div>

            <div v-else-if="apiError" class="text-center p-3">
                {{ apiError }}
            </div>

            <div v-else class="text-center p-3">
                No eToro trades found.
            </div>
        </rounded-container>

        <rounded-container v-for="section in sections" :key="section.key" variant="dark">
            <template v-slot:title>
                <span v-for="(parts, lineIndex) in section.titleLines" :key="lineIndex" class="d-block">
                    <span v-for="(part, partIndex) in parts" :key="partIndex" :class="{ 'text-white': part.highlight }">{{ part.text }}</span>
                </span>
            </template>

            <b-row
                v-for="(trade, rowIndex) in section.rows"
                :key="trade.trade_id"
                :class="[rowClass(rowIndex), { tappable: trade.asset }]"
                no-gutters
                class="p-3"
                @click="openTrade(trade)"
            >
                <b-col cols=4>
                    <b-icon :icon="sideIcon(trade)" :class="sideClass(trade)"></b-icon>&nbsp;
                    <span :class="{ 'font-weight-bold text-info': trade.asset }">{{ trade.symbol }}</span>
                    <span v-if="trade.partial" class="text-warning">&nbsp;(p)</span>
                    <b-icon v-if="trade.event" icon="link-45deg" class="text-info ml-1"></b-icon>
                </b-col>
                <b-col cols=3>
                    <span class="formatPrice">{{ formatPrice(trade.price) }}</span>
                </b-col>
                <b-col cols=3>
                    <span class="formatPrice">${{ formatNumber(trade.value) }}</span>
                </b-col>
                <b-col cols=2 class="text-secondary">
                    {{ formatTimeFromISO(trade.time) }}
                </b-col>
            </b-row>
        </rounded-container>

        <bucket-sync
            v-if="!loading"
            endpoint="/api/etoro/sync-bucket"
            label="eToro"
            :last-check="bucketLastCheck"
        />
    </div>
</template>


<script>

import apiMixin from '../mixins/api.js';
import formatterMixin from '../mixins/formatter.js';
import eventsMixin from '../mixins/eventsMixin.js';
import RoundedContainer from '../components/style/RoundedContainer.vue';
import BucketSync from '../components/widgets/BucketSync.vue';
import EtoroTrade from '../components/overlays/EtoroTrade.vue';
import { SHEET_STYLE } from '@/constants/ui';
import { TRADE_SIDE_STYLES, EVENT_TRADE_SIDES } from '@/constants/trading';

// a fill this far or further from Warren, in percent, shows in white with its distance
const FILL_DISTANCE_SHOWN = 1;

const titlePart = (text, highlight = false) => ({ text, highlight });

export default {
    name: 'EtoroTrades',
    mixins: [apiMixin, formatterMixin, eventsMixin],
    components: {
        RoundedContainer,
        BucketSync,
    },

    data() {
        return {
            loading: true,
            trades: [],
            pending: [],
            apiError: null,
            bucketLastCheck: null,
        }
    },
    methods: {
        getAssetBySymbol(symbol) {
            return this.$store.getters.getAssetBySymbol(symbol);
        },
        sideIcon(trade) {
            return TRADE_SIDE_STYLES[trade.side].icon;
        },
        sideClass(trade) {
            return TRADE_SIDE_STYLES[trade.side].class;
        },
        // a buy back comes off the exit it corrects, but only the trades on the side of the event set the price
        groupTitle(event, trades) {
            const side = EVENT_TRADE_SIDES[event.subtype];
            const size = this.eventSize(event);
            const price = this.eventPrice(event);

            const sideTrades = trades.filter(trade => trade.side === side);
            if (!sideTrades.length) return [[titlePart(`${this.formatAmount(size)} ${event.subtype} at ${this.formatPrice(price)}`)]];

            const net = trades.reduce((sum, trade) => sum + (trade.side === side ? trade.value : -trade.value), 0);
            const sideValue = sideTrades.reduce((sum, trade) => sum + trade.value, 0);
            const shares = sideTrades.reduce((sum, trade) => sum + trade.value / trade.price, 0);
            const etoroPrice = sideValue / shares;

            const sizeDistance = this.getDistance(size, net);
            const priceDistance = this.getDistance(price, etoroPrice);
            const sizeOff = Math.abs(sizeDistance.value) >= FILL_DISTANCE_SHOWN;
            const priceOff = Math.abs(priceDistance.value) >= FILL_DISTANCE_SHOWN;
            const netText = sizeOff ? `${this.formatAmount(net)} (${sizeDistance.formatted})` : this.formatAmount(net);
            const etoroPriceText = priceOff ? `${this.formatPrice(etoroPrice)} (${priceDistance.formatted})` : this.formatPrice(etoroPrice);

            return [
                [titlePart(this.formatAmount(size), sizeOff), titlePart(` ${event.subtype} at `), titlePart(this.formatPrice(price), priceOff)],
                [titlePart(netText, sizeOff), titlePart(' at '), titlePart(etoroPriceText, priceOff), titlePart(' in eToro')],
            ];
        },
        openTrade(trade) {
            // Warren holds no events for a ticker it does not know
            if (!trade.asset) return;

            this.$FModal.show(
                {
                    component: EtoroTrade,
                    ...SHEET_STYLE,
                },
                { trade, onChange: this.retrieveTrades, onLinkChange: this.setTradeEvent }
            );
        },
        // a link lives in Warren, so the row takes its new event in place instead of asking eToro for every trade again
        setTradeEvent(tradeId, event) {
            const trade = this.trades.find(line => line.trade_id === tradeId);
            if (!trade) return;

            trade.event = event;
        },
        async retrieveTrades() {
            const response = await this.apiGet('/api/etoro/trades');

            this.loading = false;
            if (!response) return;

            this.apiError = response.data.api_error || null;
            this.trades = response.data.trades;
            this.pending = response.data.pending;
            this.bucketLastCheck = response.data.bucket_last_check;
        },
    },
    created() {
        this.retrieveTrades();
    },
    computed: {
        pendingRows() {
            // an order fills once the price reaches its limit, so the move is measured from today's price
            return this.pending.map(order => {
                const asset = this.getAssetBySymbol(order.symbol);

                return {
                    ...order,
                    asset,
                    distance: asset?.price ? this.getDistance(asset.price, order.price) : null,
                };
            });
        },
        tradeRows() {
            /* eToro names an instrument its own way, so a ticker it holds under another
               name has no Warren asset and shows as plain text. */
            return this.trades.map(trade => ({
                ...trade,
                asset: this.getAssetBySymbol(trade.symbol),
            }));
        },
        // the unlinked trades come first, then one section per Warren event with the newest event on top
        sections() {
            const unlinked = this.tradeRows.filter(trade => !trade.event);

            const groups = {};
            this.tradeRows.filter(trade => trade.event).forEach(trade => {
                const key = trade.event.public_id;
                groups[key] = groups[key] || { key, event: trade.event, rows: [] };
                groups[key].rows.push(trade);
            });

            const eventSections = Object.values(groups)
                .sort((a, b) => this.dateFromISO(b.event.timestamp) - this.dateFromISO(a.event.timestamp))
                .map(({ key, event, rows }) => ({ key, titleLines: this.groupTitle(event, rows), rows }));

            if (!unlinked.length) return eventSections;

            return [{ key: 'unlinked', titleLines: [[titlePart('Unlinked')]], rows: unlinked }, ...eventSections];
        },
    }
}

</script>

<style scoped>
.tappable {
    cursor: pointer;
}
</style>
